import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LayoutSkeleton from '../pages/skeleton/LayoutSkeleton';
import { useNotifications } from '../context/NotificationsContext.jsx';
import { useSocket } from '../context/SocketContext';
import ConfirmLogoutModal from './ConfirmLogoutModal';
import { clearAppState } from '../services/persistenceService';

const Navbar = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [userName, setUserName] = useState("")
    const [userBalance, setUserBalance] = useState(0)
    const [userAvatarIndex, setUserAvatarIndex] = useState(null);
    const { unReadCount } = useNotifications();
    const { socket, isConnected } = useSocket();
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const contentRef = useRef(null);

    // Page Transition Animation removed to fix visibility issues
    useEffect(() => {
        // Animation removed as per user request to fix visibility issues
    }, [location.pathname]);

    useEffect(() => {
        if (!socket) {
            return;
        }

        // Listen for wallet balance updates
        const handleWalletUpdate = (data) => {
            const newBalance = data.availableBalance || data.aviableBalance || 0;
            if (newBalance !== undefined && newBalance !== null) {
                setUserBalance(Number(newBalance));
            }
        };

        // Listen for payment status updates
        const handlePaymentStatusUpdate = (data) => {
            if (data.status === 'completed') {
                fetchUserWallet();
            }
        };

        socket.on('wallet-updated', handleWalletUpdate);
        socket.on('payment-status-updated', handlePaymentStatusUpdate);

        return () => {
            socket.off('wallet-updated', handleWalletUpdate);
            socket.off('payment-status-updated', handlePaymentStatusUpdate);
        };
    }, [socket]);

    const fetchUserWallet = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/profile`);
            if (response.data.success) {
                const user = response.data.user;
                setUserName(user.username);
                const balance = user.walletData?.availableBalance || user.walletData?.aviableBalance || 0;
                setUserBalance(Number(balance));
                setUserAvatarIndex(user.avatarIndex);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                navigate("/auth");
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchUserWallet();
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const currentPath = location.pathname;
        const triggerPages = ['/wallet', '/add', '/withdraw', '/profile', '/home', '/join-tournament'];
        const shouldRefresh = triggerPages.some(page => currentPath.startsWith(page)) || currentPath === '/home';

        if (shouldRefresh) {
            fetchUserWallet();
        }
    }, [location.pathname]);

    const mobileMenuItems = [
        { icon: 'ri-home-5-line', label: 'Home', path: '/home' },
        { icon: 'ri-sword-line', label: 'Matches', path: '/my-matches' },
        { icon: 'ri-wallet-3-line', label: 'Wallet', path: '/wallet' },
        { icon: 'ri-line-chart-line', label: 'Leaderboard', path: '/leaderboard' },
    ];

    const desktopMenuItems = [
        { icon: 'ri-home-5-line', label: 'Home', path: '/home' },
        { icon: 'ri-sword-line', label: 'My Matches', path: '/my-matches' },
        { icon: 'ri-wallet-3-line', label: 'Wallet', path: '/wallet' },
        { icon: 'ri-line-chart-line', label: 'Leaderboard', path: '/leaderboard' },
        { icon: 'ri-user-line', label: 'Profile', path: '/profile' },
        { icon: 'ri-notification-line', label: 'Notifications', path: '/notifications' },
        { icon: 'ri-logout-box-r-line', label: 'Logout', path: '/auth' }
    ];

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleLogoutClick = () => {
        setIsLogoutModalOpen(true);
    };

    const handleLogoutConfirm = async () => {
        setIsLoggingOut(true);
        try {
            await axios.post(`${backendUrl}/api/logout`, {}, { withCredentials: true });
        } catch (error) {
        } finally {
            setIsLoggingOut(false);
            setIsLogoutModalOpen(false);
            localStorage.removeItem('ff_arena_user_session'); // ✅ CLEAR PERSISTENT SESSION
            clearAppState();
            navigate("/auth");
        }
    };

    const isActivePath = (path) => {
        const currentPath = location.pathname;
        if (path === '/wallet') {
            return currentPath === '/wallet' ||
                currentPath.includes('/wallet/') ||
                currentPath === '/add' ||
                currentPath === '/withdraw';
        }
        return currentPath === path;
    };

    const showNotification = (message, type = 'info') => {
        const notification = document.createElement('div');
        notification.className = `fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border-l-4 max-w-sm ${type === 'success' ? 'bg-green-500/20 border-green-500 text-green-300' :
            type === 'error' ? 'bg-red-500/20 border-red-500 text-red-300' :
                'bg-blue-500/20 border-blue-500 text-blue-300'
            }`;

        notification.innerHTML = `
            <div class="flex items-center space-x-2">
                <i class="ri-${type === 'success' ? 'check-line' : type === 'error' ? 'close-line' : 'information-line'}"></i>
                <span class="text-sm font-medium">${message}</span>
            </div>
        `;

        document.body.appendChild(notification);
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 3000);
    };

    return (
        <>
            <ConfirmLogoutModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={handleLogoutConfirm}
                isLoading={isLoggingOut}
            />

            {loading ? (
                <LayoutSkeleton />
            ) : (
                <div className="bg-gray-900 min-h-screen text-white">
                    <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 fixed top-0 left-0 right-0 lg:left-64 z-40">
                        <div className="flex items-center justify-between">
                            <div
                                onClick={() => handleNavigation('/home')}
                                className="flex items-center space-x-2 cursor-pointer group"
                            >
                                <i className="ri-sword-fill text-2xl text-red-500 group-hover:scale-110 transition-transform"></i>
                                <span className="text-xl font-bold text-white group-hover:text-red-500 transition-colors">FF ARENA</span>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="hidden sm:flex items-center space-x-2 bg-gray-700 px-3 py-1 rounded-lg">
                                    <i className="ri-wallet-3-line text-yellow-400"></i>
                                    <span className="text-yellow-400 font-semibold">₹{userBalance.toLocaleString()}</span>
                                </div>
                                <button
                                    onClick={() => handleNavigation('/notifications')}
                                    className="relative p-2 rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    <i className="ri-notification-line text-xl"></i>
                                    {unReadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            {unReadCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => handleNavigation('/profile')}
                                    className="w-10 h-10 rounded-lg overflow-hidden border border-gray-700 hover:border-gray-600 transition-colors"
                                >
                                    <img
                                        src={`/avatars/avatar${userAvatarIndex || 1}.png`}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            </div>
                        </div>
                    </header>

                    <aside className="hidden lg:block w-64 bg-gray-800 border-r border-gray-700 min-h-screen fixed left-0 top-0 bottom-0 overflow-y-auto z-30">
                        <div className="p-6 border-b border-gray-700">
                            <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden border border-gray-600">
                                    <img
                                        src={`/avatars/avatar${userAvatarIndex || 1}.png`}
                                        alt="User"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h3 className="font-bold text-white uppercase">Welcome</h3>

                                </div>
                            </div>
                        </div>

                        <nav className="p-4 space-y-2">
                            {desktopMenuItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() =>
                                        item.path === "/auth" ? handleLogoutClick() : handleNavigation(item.path)
                                    }
                                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors group text-left ${isActivePath(item.path)
                                        ? "bg-gradient-to-r from-red-500 to-yellow-500 text-white shadow-lg"
                                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                                        }`}
                                >
                                    <i className={`${item.icon} text-xl ${isActivePath(item.path) ? "text-white" : "group-hover:text-yellow-400"}`}></i>
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            ))}
                        </nav>
                    </aside>

                    <main className="flex-1 lg:ml-64 mt-16 lg:mt-16 pb-20 lg:pb-6 overflow-auto">
                        <div
                            ref={contentRef}
                            className="bg-gray-800 rounded-lg px-0 min-h-[calc(100vh-140px)]"
                        >
                            {children}
                        </div>
                    </main>

                    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 z-40">
                        <div className="flex justify-around items-center p-3">
                            {mobileMenuItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() => handleNavigation(item.path)}
                                    className={`flex flex-col items-center space-y-1 p-2 rounded-lg transition-colors group relative ${isActivePath(item.path)
                                        ? 'bg-gradient-to-r from-red-500 to-yellow-500 text-white'
                                        : 'hover:bg-gray-700'
                                        }`}
                                >
                                    <i className={`${item.icon} text-xl ${isActivePath(item.path) ? 'text-white' : 'group-hover:text-yellow-400'}`}></i>
                                    <span className={`text-xs ${isActivePath(item.path) ? 'text-white' : 'group-hover:text-yellow-400'}`}>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </nav>
                </div>
            )}
        </>
    );
};

export default Navbar;
